import { CalendarContext } from '../../../../utils/context/contextFactory';
import {
  getDurationFromMinutes,
  getSlotDuration,
} from '../../../../utils/duration/duration';
import { CalendarState } from '../../controller';
import { MemoizedViewModalFactory } from '../viewModel';
import { LocationType } from '@wix/ambassador-bookings-services-v2-service/types';

export interface SummaryViewModel {
  openPreferencesButton: {
    text: string;
    ariaLabel: string;
  };
  summary: {
    services?: string;
    duration?: ReturnType<typeof getDurationFromMinutes>;
    location: string;
    addOnsCount?: string;
  };
  isRescheduleFlow?: boolean;
}

export const memoizedSummaryViewModel: MemoizedViewModalFactory<SummaryViewModel> =
  {
    dependencies: {
      state: [
        'servicesInView',
        'selectableSlotsAtSelectedTime',
        'rescheduleBookingDetails',
      ],
    },
    createViewModel: createSummaryViewModel,
  };

export function createSummaryViewModel({
  state,
  context,
}: {
  state: CalendarState;
  context: CalendarContext;
}): SummaryViewModel {
  const { t } = context;

  return {
    openPreferencesButton: {
      text: t('app.summary.open-preferences-modal-button.text'),
      ariaLabel: t('app.summary.open-preferences-modal-button.aria-label'),
    },
    summary: {
      duration: getDurationSummary({ context, state }),
      services: getServicesSummary({ context, state }),
      location: getLocationSummary({ context, state }),
      addOnsCount: getAddonsCount({ context }),
    },
    isRescheduleFlow: !!state.rescheduleBookingDetails,
  };
}

const getLocationSummary = ({
  context,
  state,
}: {
  state: CalendarState;
  context: CalendarContext;
}) => {
  const { t, calendarSelections } = context;
  const { servicesInView } = state;
  const service = servicesInView[0];

  const { location: locationId } = calendarSelections!;

  const selectedLocation = locationId
    ? service.locations?.find((location) => location.id === locationId)
    : service.locations![0];

  const locationType = selectedLocation?.type;

  if (locationType === LocationType.BUSINESS) {
    return selectedLocation?.business?.name!;
  }

  if (locationType === LocationType.CUSTOM) {
    return selectedLocation?.calculatedAddress?.formattedAddress!;
  }

  if (locationType === LocationType.CUSTOMER) {
    return t('app.booking-details.dropdowns.locations.client-place.text');
  }

  return '';
};

const getServicesSummary = ({
  context,
  state,
}: {
  state: CalendarState;
  context: CalendarContext;
}) => {
  const { t, calendarSelections } = context;
  const { servicesInView } = state;

  const { services } = calendarSelections!;
  const numberOfServices = services.length;

  if (numberOfServices === 1) {
    const serviceId = services[0].id;

    const service = servicesInView.find(({ id }) => id === serviceId);
    if (service) {
      return service.name!;
    }
  }

  return t('app.summary.selected-services', { numberOfServices });
};

const getDurationSummary = ({
  context,
  state,
}: {
  state: CalendarState;
  context: CalendarContext;
}) => {
  const { selectableSlotsAtSelectedTime } = state;
  const { t, languageSettings } = context;

  const dateRegionalSettingsLocale = languageSettings.regionalSettingsLocale!;

  if (!selectableSlotsAtSelectedTime?.length) {
    return undefined;
  }

  const [{ localStartDate: startDate, localEndDate: endDate }] =
    selectableSlotsAtSelectedTime;

  const isAllSlotDurationEqual = selectableSlotsAtSelectedTime.every(
    (slot) =>
      slot?.localStartDate === startDate && slot?.localEndDate === endDate,
  );

  if (!isAllSlotDurationEqual) {
    return undefined;
  }

  return getSlotDuration({
    dateRegionalSettingsLocale,
    t,
    rfcStartTime: startDate!,
    rfcEndTime: endDate!,
  });
};

const getAddonsCount = ({ context }: { context: CalendarContext }) => {
  const { calendarSelections, t } = context;

  if (!calendarSelections?.services) {
    return undefined;
  }

  const totalAddonsCount = calendarSelections.services.reduce(
    (total, { addOns }) => total + (addOns?.length || 0),
    0,
  );

  if (totalAddonsCount <= 0) {
    return undefined;
  }

  return t('app.summary.addons-count', { count: totalAddonsCount });
};
